'use client';

import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FilterParams } from '@/lib/search-filter-params';
import {
  countFiltersByGroup,
  emptyFilterValue,
  FILTER_GROUP_IDS,
  FILTER_KEYS,
  groupFilterKeys,
  type FilterGroupId,
  type FilterKey,
} from '@/lib/search-filter-defs';
import { filterLocationsForCountry } from './searchConstants';

/** The sidebar's staged filters: what its controls show until Search applies them. */
export type StagedFilters = FilterParams;

/** One stable setter per staged field — `set.hasEmail(true)` is `setFilter('hasEmail', true)`. */
export type StagedFilterSetters = {
  readonly [K in FilterKey]-?: Dispatch<SetStateAction<FilterParams[K]>>;
};

/** `filterParams` as staged values, a missing field at its empty value. */
function stagedFrom(filterParams: FilterParams | undefined): StagedFilters {
  return Object.fromEntries(
    FILTER_KEYS.map((key) => [key, filterParams?.[key] ?? emptyFilterValue(key)]),
  ) as unknown as StagedFilters;
}

/** `prev` with `patch` applied, or `prev` itself when no value changes. */
function patchStaged(prev: StagedFilters, patch: Partial<StagedFilters>): StagedFilters {
  const keys = Object.keys(patch) as FilterKey[];
  return keys.every((key) => Object.is(prev[key], patch[key])) ? prev : { ...prev, ...patch };
}

/**
 * The sidebar's staged filter values: one state object shaped like
 * FilterParams, seeded from the applied `filterParams` and re-synced whenever
 * the applied URL changes. `setFilter(key, value)` (or `set[key]`) edits one
 * field; a new filter needs nothing here — FILTER_DEFS drives it.
 */
export function useSidebarFilterValues(
  filterParams: FilterParams | undefined,
  preserveCountryEdits: boolean,
) {
  const [staged, setStaged] = useState<StagedFilters>(() => stagedFrom(filterParams));
  const setFilter = useCallback(
    <K extends FilterKey>(key: K, value: SetStateAction<FilterParams[K]>) => {
      setStaged((prev) => {
        const next = typeof value === 'function' ? value(prev[key]) : value;
        return patchStaged(prev, { [key]: next });
      });
    },
    [],
  );
  const set = useMemo(
    () =>
      Object.fromEntries(
        FILTER_KEYS.map((key) => [key, (value: SetStateAction<FilterParams[FilterKey]>) => setFilter(key, value)]),
      ) as unknown as StagedFilterSetters,
    [setFilter],
  );
  // Industries are an independent filter — sent to the backend as a separate
  // parameter that gets expanded to NACE divisions there. The NACE-Codes
  // selector is unaffected by industry toggles, and vice versa.
  const toggleIndustry = useCallback(
    (key: string) => {
      setFilter('industries', (prev) =>
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
      );
    },
    [setFilter],
  );

  const previousAppliedFilters = useRef(filterParams);

  // Staged edits leave filterParams unchanged. Sync whenever the applied URL
  // changes, including chip removals and back/forward navigation.
  useEffect(() => {
    const previous = previousAppliedFilters.current;
    previousAppliedFilters.current = filterParams;
    if (filterParams) {
      const nextCountry = filterParams.country ?? '';
      const scopeOnly = isScopeOnlyChange(previous, filterParams, nextCountry, preserveCountryEdits);
      // Staged filters re-seed from the applied URL after it changes.
      setStaged((prev) =>
        patchStaged(prev, scopeOnly ? scopeChange(prev, nextCountry) : stagedFrom(filterParams)),
      );
    }
  }, [filterParams, preserveCountryEdits]);

  return { staged, set, setFilter, toggleIndustry };
}

export type SidebarFilterValues = ReturnType<typeof useSidebarFilterValues>;

/**
 * A scope-only change applies immediately but keeps the other staged edits:
 * true when the newly applied filters equal the previous ones with just the
 * country moved. Staged locations are narrowed just like the applied result
 * filters.
 */
function isScopeOnlyChange(
  previous: FilterParams | undefined,
  filterParams: FilterParams,
  nextCountry: string,
  preserveCountryEdits: boolean,
): boolean {
  const expected = previous && {
    ...previous,
    country: nextCountry,
    Bundesland: filterLocationsForCountry(previous.Bundesland, nextCountry),
    city: nextCountry ? [] : previous.city,
  };
  return Boolean(
    preserveCountryEdits &&
    previous &&
    previous.country !== nextCountry &&
    Object.keys(filterParams).every(
      (key) =>
        JSON.stringify(filterParams[key as keyof FilterParams]) ===
        JSON.stringify(expected?.[key as keyof FilterParams]),
    ),
  );
}

/** The staged edits a scope-only change makes: the country, and the locations it rules out. */
function scopeChange(staged: StagedFilters, nextCountry: string): Partial<StagedFilters> {
  return {
    country: nextCountry,
    Bundesland: filterLocationsForCountry(staged.Bundesland, nextCountry),
    ...(nextCountry && { city: [] }),
  };
}

/** The staged values as one FilterParams — what Search, Save and the footer emit. */
export function useCollectFilters({ staged }: SidebarFilterValues) {
  return useCallback((): FilterParams => ({ ...staged }), [staged]);
}

/** Per-group active filter counts for the group headers (computed each render). */
export function countActiveFilters({ staged }: SidebarFilterValues): Record<FilterGroupId, number> {
  // Each non-empty field counts once. The keyword input sits outside every
  // group and isn't counted; FILTER_DEFS assigns the rest to their group.
  return countFiltersByGroup(staged);
}

/**
 * The per-group "clear" actions: each resets the fields FILTER_DEFS assigns
 * to its group. `setFilter` is stable, so each callback keeps one identity for
 * the component's lifetime.
 */
export function useFilterGroupClears({ setFilter }: SidebarFilterValues): Record<FilterGroupId, () => void> {
  return useMemo(
    () =>
      Object.fromEntries(
        FILTER_GROUP_IDS.map((group) => [
          group,
          () => {
            for (const key of groupFilterKeys(group)) setFilter(key, emptyFilterValue(key));
          },
        ]),
      ) as Record<FilterGroupId, () => void>,
    [setFilter],
  );
}
